import { MultiplayerRuntime } from "../MultiplayerRuntime";
import {
  createMultiplayerRuntimeHandle,
  MultiplayerRuntimeHandle,
} from "../handle/MultiplayerRuntimeHandle";
import { GameState } from "../../../core/protocol/types";
import { MultiplayerOrchestrator } from "../orchestrator/MultiplayerOrchestrator";

export interface JoinRuntimeConnectOptions {
  hostId: string;
  playerName: string;
  team?: number;
  wsUrl?: string; // Optional WebSocket URL for dedicated server
}

export type JoinRuntimeStatusEvent =
  | { type: "idle" }
  | { type: "connecting"; hostId: string }
  | { type: "awaiting-acceptance"; hostId: string }
  | { type: "downloading"; hostId: string; progress: number }
  | { type: "ready"; hostId: string }
  | { type: "error"; message: string };

export class JoinRuntimeSession {
  private runtime: MultiplayerRuntime | null = null;
  private handle: MultiplayerRuntimeHandle | null = null;
  private orchestrator: MultiplayerOrchestrator | null = null;
  private status: JoinRuntimeStatusEvent = { type: "idle" };
  private statusListeners: Set<(event: JoinRuntimeStatusEvent) => void> =
    new Set();
  private downloadListeners: Set<(progress: number) => void> = new Set();
  private readyListeners: Set<(orchestrator: MultiplayerOrchestrator) => void> =
    new Set();
  private unsubscribeState?: () => void;
  private readyPromise?: Promise<MultiplayerOrchestrator>;
  private resolveReady?: (orchestrator: MultiplayerOrchestrator) => void;
  private rejectReady?: (error: Error) => void;
  private hostId: string | null = null;
  private downloadStarted = false;
  private downloadComplete = false;

  connect(
    options: JoinRuntimeConnectOptions
  ): Promise<MultiplayerOrchestrator> {
    if (this.orchestrator && this.downloadComplete) {
      return Promise.resolve(this.orchestrator);
    }

    if (this.readyPromise) {
      return this.readyPromise;
    }

    this.hostId = options.hostId;
    this.emitStatus({ type: "connecting", hostId: options.hostId });

    this.runtime = new MultiplayerRuntime();
    this.handle = createMultiplayerRuntimeHandle(this.runtime);
    this.orchestrator = new MultiplayerOrchestrator({
      runtime: this.runtime,
      handle: this.handle,
    });

    this.watchRuntimeState();

    const controller = this.handle.controller;

    this.readyPromise = new Promise<MultiplayerOrchestrator>(
      (resolve, reject) => {
        this.resolveReady = resolve;
        this.rejectReady = reject;
      }
    );

    controller
      .joinGame(options.hostId, options.playerName, options.wsUrl)
      .then(() => {
        this.emitStatus({ type: "awaiting-acceptance", hostId: options.hostId });
      })
      .catch((error) => {
        const err =
          error instanceof Error
            ? error
            : new Error("Failed to connect to host");
        this.emitStatus({ type: "error", message: err.message });
        this.cleanup();
        this.rejectReady?.(err);
        this.readyPromise = undefined;
        this.resolveReady = undefined;
        this.rejectReady = undefined;
      });

    return this.readyPromise;
  }

  cancel(): void {
    this.cleanup();
    const error = new Error("Join flow cancelled");
    this.rejectReady?.(error);
    this.readyPromise = undefined;
    this.resolveReady = undefined;
    this.rejectReady = undefined;
    this.emitStatus({ type: "idle" });
  }

  onStatus(listener: (event: JoinRuntimeStatusEvent) => void): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  onDownloadProgress(listener: (progress: number) => void): () => void {
    this.downloadListeners.add(listener);
    return () => {
      this.downloadListeners.delete(listener);
    };
  }

  onReady(
    listener: (orchestrator: MultiplayerOrchestrator) => void
  ): () => void {
    this.readyListeners.add(listener);
    if (this.orchestrator && this.downloadComplete) {
      listener(this.orchestrator);
    }
    return () => {
      this.readyListeners.delete(listener);
    };
  }

  private watchRuntimeState(): void {
    if (!this.runtime || !this.hostId) {
      return;
    }

    this.unsubscribeState = this.runtime.subscribe((state) => {
      if (!state || !this.handle) {
        return;
      }

      if (!this.downloadStarted && state.localConnectionId) {
        this.downloadStarted = true;
      }

      if (this.downloadStarted && !this.downloadComplete) {
        const progress = this.calculateDownloadProgress(state);
        this.emitDownload(progress);
        this.emitStatus({
          type: "downloading",
          hostId: this.hostId!,
          progress,
        });

        if (progress >= 1) {
          this.downloadComplete = true;
          this.emitStatus({ type: "ready", hostId: this.hostId! });
          if (this.orchestrator) {
            this.readyListeners.forEach((listener) => listener(this.orchestrator!));
            this.orchestrator.setRuntimeReady(true);
            this.resolveReady?.(this.orchestrator);
          }
          this.readyPromise = undefined;
          this.resolveReady = undefined;
          this.rejectReady = undefined;
        }
      }
    });
  }

  private calculateDownloadProgress(state: GameState): number {
    if (!this.handle) {
      return 0;
    }

    const remoteManager = this.handle.managers.remote;
    const players = Array.from(state.players.values()).filter((player) => {
      if (player.isDedicatedServer) {
        return false;
      }
      return player.connectionId !== state.localConnectionId;
    });

    if (players.length === 0) {
      return 1;
    }

    let readyCount = 0;
    for (const player of players) {
      const remoteGame = remoteManager.getRemoteGameByNumPlayer(player.numPlayer);
      if (!remoteGame) {
        break;
      }
      if (remoteGame.canvas && remoteGame.canvas.waitDownload === false) {
        readyCount += 1;
      } else {
        break;
      }
    }

    if (readyCount === players.length) {
      return 1;
    }

    return readyCount / players.length;
  }

  private emitStatus(event: JoinRuntimeStatusEvent): void {
    this.status = event;
    this.statusListeners.forEach((listener) => listener(event));
  }

  private emitDownload(progress: number): void {
    this.downloadListeners.forEach((listener) => listener(progress));
  }

  private cleanup(): void {
    if (this.unsubscribeState) {
      this.unsubscribeState();
      this.unsubscribeState = undefined;
    }
    if (this.orchestrator) {
      this.orchestrator.dispose();
      this.orchestrator = null;
      this.handle = null;
      this.runtime = null;
    } else {
      if (this.handle) {
        this.handle.dispose();
        this.handle = null;
      }
      this.runtime = null;
    }
    this.downloadStarted = false;
    this.downloadComplete = false;
    this.hostId = null;
  }
}


